import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { setTenantId } from './services/api';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function applyTenantMeta(tenantData) {
  if (tenantData.primary_color) {
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.content = tenantData.primary_color;
    const root = document.documentElement;
    root.style.setProperty('--color-primary', tenantData.primary_color);
    root.style.setProperty('--color-primary-dark', tenantData.primary_color);
  }
  if (tenantData.secondary_color) {
    document.documentElement.style.setProperty('--color-secondary', tenantData.secondary_color);
  }
  const appTitle = tenantData.pwa_app_name || tenantData.name;
  if (appTitle) {
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitleMeta) appleTitleMeta.content = appTitle;
    document.title = appTitle;
  }
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Load tenant branding from localStorage, then fetch from API
  useEffect(() => {
    const saved = localStorage.getItem('spe_tenant');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setTenant(parsed);
        setTenantId(parsed.id);
      } catch {}
    }

    // Set dynamic manifest link
    const manifestLink = document.getElementById('pwa-manifest');
    if (manifestLink) {
      manifestLink.href = `${API_URL}/manifest.json`;
    }

    // Fetch tenant info from API (based on current domain)
    fetch(`${API_URL}/tenant/info`)
      .then(res => res.json())
      .then(data => {
        if (data?.name) {
          setTenant(data);
          setTenantId(data.id);
          localStorage.setItem('spe_tenant', JSON.stringify(data));
          applyTenantMeta(data);
        }
      })
      .catch(() => {});
  }, []);

  // Listen for tenant updates from CampaignPage
  useEffect(() => {
    const handler = (e) => {
      setTenant(e.detail);
      applyTenantMeta(e.detail);
    };
    window.addEventListener('tenant-loaded', handler);
    return () => window.removeEventListener('tenant-loaded', handler);
  }, []);

  // Detect scroll for header background
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const logoUrl = tenant?.logo_url;
  const tenantName = tenant?.name || 'SPE Royalti';
  const subtitle = tenant?.subtitle;

  return (
    <div className="app">
      <header className={`app-header animate-fade-in-down ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-inner">
          {/* Spacer to balance hamburger on the right */}
          <div className="header-spacer" />

          <NavLink to="/" className="header-brand">
            {logoUrl ? (
              <img src={logoUrl} alt={tenantName} className="header-logo" />
            ) : (
              <span className="header-title">{tenantName}</span>
            )}
            {subtitle && <span className="header-subtitle">{subtitle}</span>}
          </NavLink>

          <button
            className="hamburger-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
          </button>
        </div>
      </header>

      {/* Slide-in menu overlay */}
      {menuOpen && <div className="menu-overlay" onClick={() => setMenuOpen(false)} />}
      <nav className={`slide-menu ${menuOpen ? 'open' : ''}`}>
        <div className="slide-menu-header">
          {logoUrl ? (
            <img src={logoUrl} alt={tenantName} className="menu-logo" />
          ) : (
            <span className="menu-brand">{tenantName}</span>
          )}
        </div>
        <NavLink to="/" end className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Utama
        </NavLink>
        <NavLink to="/check" className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
          </svg>
          Semak Voucher
        </NavLink>
        <NavLink to="/profile" className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          Maklumat Saya
        </NavLink>
        <NavLink to="/privacy" className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          Dasar Privasi
        </NavLink>
      </nav>

      <main className="app-main">
        <Outlet />
      </main>

      <footer className="app-footer">
        <p>&copy; {new Date().getFullYear()} {tenantName}</p>
        <div className='sub-footer'>Apps by Onewoorks Solutions</div>
      </footer>
    </div>
  );
}

export default App;
